import { Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, Heart } from "lucide-react";
import { useSEO } from "@/lib/seo";

/**
 * Early-access safety + privacy page.
 * Plain English, no legalese, single-screen scannable. The file name
 * (DemoSafety.jsx) and route (/demo-safety) are preserved to keep inbound
 * bookmarks and the test suite working; the user-visible copy has flipped
 * from "demo" to "early access" wording.
 */
export default function DemoSafety() {
  useSEO({
    title: "Early access · safety & privacy",
    description:
      "SonSpeak is in early access — not a medical, clinical, or emergency tool.",
    path: "/demo-safety",
  });

  return (
    <div className="min-h-screen dotted-bg" data-testid="demo-safety-page">
      <main className="max-w-3xl mx-auto px-5 sm:px-10 py-10 sm:py-14">
        <div className="display text-xs uppercase tracking-[0.3em] text-[color:var(--coral-deep)] font-bold mb-3">
          Important — please read
        </div>
        <h1 className="display text-4xl sm:text-5xl font-extrabold leading-tight mb-6">
          Early access. Please read before signing up.
        </h1>

        <section
          className="brutal-card bg-white p-5 mb-6"
          style={{ borderColor: "var(--coral-deep)" }}
        >
          <h2
            className="display text-xl font-extrabold flex items-center gap-2 mb-2"
            style={{ color: "var(--coral-deep)" }}
          >
            <AlertTriangle size={20} /> Not a medical or clinical tool
          </h2>
          <p className="leading-relaxed" style={{ color: "var(--ink)" }}>
            SonSpeak is in <strong>early access</strong>. It is{" "}
            <strong>not</strong> a regulated medical device, FDA-cleared
            assistive technology, or substitute for a Speech-Language Pathologist.
            <strong> Do not rely on SonSpeak for real-world care, therapy
            decisions, or emergencies.</strong> If you or your child are
            experiencing a medical or safety emergency, call 911 (US) or your
            local emergency number — never use this app for that.
          </p>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
            <ShieldCheck size={20} className="text-[color:var(--coral)]" />
            What I store — and what I don't
          </h2>
          <ul className="space-y-2 text-base leading-relaxed list-disc pl-5">
            <li>
              <strong>Accounts are real but minimal</strong> — email + a
              hashed password, no real-name verification, no payment info on
              file until you actively start a paid plan.
            </li>
            <li>
              <strong>Welcome / reset emails:</strong> during early access,
              transactional email is in <em>sandbox mode</em>. Emails route
              to the founder's inbox first; nothing leaves the platform until
              the production domain is verified.
            </li>
            <li>
              <strong>Tile photos &amp; voice recordings</strong> stay scoped
              to your account and can be wiped from{" "}
              <code className="bg-[color:var(--paper-muted)] px-1 rounded">
                Settings → Danger zone → Delete my account
              </code>{" "}
              in one tap.
            </li>
            <li>
              <strong>No advertising. No third-party trackers.</strong> No
              child voice or photo is ever used to train AI.
            </li>
            <li>
              <strong>Therapist / clinic features</strong> visible in early
              access are a <em>preview of planned production functionality</em>.
              Connecting a real school, clinic, or licensed clinician is not
              available in this preview.
            </li>
          </ul>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-extrabold mb-3">
            Please do <span className="text-[color:var(--coral-deep)]">NOT</span> enter:
          </h2>
          <ul className="space-y-2 text-base leading-relaxed list-disc pl-5">
            <li>Real personal medical or diagnostic information</li>
            <li>School names, clinic names, IEP details, or staff names</li>
            <li>
              A child's last name, address, phone number, or government IDs
            </li>
            <li>Anything you would not be comfortable seeing on a public page</li>
          </ul>
          <p className="text-sm text-[color:var(--ink-soft)] mt-3 italic">
            SonSpeak auto-blocks obvious last-name/school/address patterns on
            public family pages, but please don't test those filters with real
            information.
          </p>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h2 className="display text-xl font-extrabold mb-3">
            Send me feedback
          </h2>
          <p className="leading-relaxed">
            If you find a bug, want to suggest a feature, or want to support
            this work, email me directly at{" "}
            <a
              href="mailto:hello@sonspeak.com"
              className="underline font-semibold"
              data-testid="demo-safety-contact"
            >
              hello@sonspeak.com
            </a>
            .{" "}
            <strong>
              Please don't include personal, medical, or school information in
              your message.
            </strong>{" "}
            Product feedback only — I'll ask separately if I ever need anything
            sensitive.
          </p>
        </section>

        <section className="brutal-card bg-[color:var(--cream-deep)] p-5 mb-8 flex items-start gap-3">
          <Heart size={22} className="text-[color:var(--coral)] shrink-0 mt-1" fill="currentColor" />
          <p className="leading-relaxed">
            SonSpeak is currently in early access. The production version
            will ship with a full privacy policy, clinical review by a
            licensed Speech-Language Pathologist, and data-portability
            tools. Until then,{" "}
            <strong>treat this app like the prototype it is.</strong>
          </p>
        </section>

        <div className="flex flex-wrap gap-3">
          <Link to="/" className="pill-btn primary" data-testid="demo-safety-back">
            Back to home
          </Link>
          <Link to="/privacy" className="pill-btn" data-testid="demo-safety-privacy-link">
            Full privacy policy
          </Link>
        </div>
      </main>
    </div>
  );
}
